/**
 * Utility to display friendly names for database field names
 * This utility can be imported anywhere in the application to ensure consistent display names
 * for database fields and other technical terms.
 */

// Main field name mapping object
const fieldNameMappings = {
  // Employee data fields
  employee_id: "Employee ID",
  name: "Full Name",
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email Address",
  store: "Location",
  position: "Job Title",
  pay_rate: "Pay Rate",
  pay_frequency: "Pay Frequency",
  phone: "Phone Number",
  address: "Street Address",
  old_address: "Previous Address",
  city: "City",
  state: "State",
  zip: "ZIP Code",
  hire_date: "Hire Date",
  termination_date: "Termination Date",
  status: "Employment Status",
  ssn: "Social Security Number",
  dob: "Date of Birth",
  emergency_contact: "Emergency Contact",
  emergency_phone: "Emergency Contact Phone",
  emergency_relationship: "Emergency Contact Relationship",
  bank_info: "Banking Information",
  bank_account: "Bank Account Number",
  bank_routing: "Bank Routing Number",
  w4_status: "W-4 Status",
  w4_allowances: "W-4 Allowances",
  role: "System Role",
  
  // Form-related fields
  form_id: "Form ID",
  form_name: "Form Name",
  form_type: "Form Type",
  form_status: "Form Status",
  submission_date: "Submission Date",
  approval_date: "Approval Date",
  approved_by: "Approved By",
  form_data: "Form Data",
  
  // Payment/Check fields
  check_id: "Check ID",
  check_number: "Check Number",
  check_date: "Check Date",
  check_amount: "Check Amount",
  paper_check: "Paper Check",
  direct_deposit: "Direct Deposit",
  gross_pay: "Gross Pay",
  net_pay: "Net Pay",
  pay_period_start: "Pay Period Start",
  pay_period_end: "Pay Period End",
  pay_date: "Pay Date",
  hours_worked: "Hours Worked",
  overtime_hours: "Overtime Hours",
  regular_rate: "Regular Rate",
  overtime_rate: "Overtime Rate",
  federal_tax: "Federal Tax",
  state_tax: "State Tax",
  local_tax: "Local Tax",
  social_security: "Social Security",
  medicare: "Medicare",
  futa: "FUTA",
  sui: "SUI",
  deductions: "Deductions",
  
  // Note/Comment fields
  note_id: "Note ID",
  note_date: "Note Date",
  subject: "Subject",
  content: "Content",
  created_at: "Created At",
  updated_at: "Updated At",
  created_by: "Created By",
  updated_by: "Updated By",
  
  // Todo fields
  todo_id: "Todo ID",
  description: "Description",
  due_date: "Due Date",
  is_completed: "Completed",
  priority: "Priority",
  assigned_to: "Assigned To",
  assigned_by: "Assigned By",
  completion_date: "Completion Date",
  
  // Check request fields
  never_received: "Never received check",
  damaged_lost: "Check was damaged/lost",
  old_address: "Check sent to previous address",
  paper_check: "Paper Check",
  wire_payment: "Wire Payment",
  
  // Request types
  check: "Check Request",
  note: "General Note",
  sick_time: "Sick Time Request",
  
  // Request status
  pending: "Pending",
  approved: "Approved",
  denied: "Denied",
  
  // Generic database fields
  id: "ID",
  timestamp: "Timestamp",
  active: "Active"
};

/**
 * Convert a database field name to a human-readable friendly name
 * 
 * @param {string} fieldName - The database field name to convert
 * @returns {string} - Human-readable friendly name
 */
const getFriendlyFieldName = (fieldName) => {
  if (!fieldName) return '';
  
  // First check the mapping object
  if (fieldNameMappings[fieldName]) {
    return fieldNameMappings[fieldName];
  }
  
  // For snake_case fields without explicit mapping, convert to Title Case
  if (fieldName.includes('_')) {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  // For camelCase fields without explicit mapping
  if (/[a-z][A-Z]/.test(fieldName)) {
    return fieldName
      // Insert a space before all uppercase letters that follow lowercase letters
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Capitalize the first letter
      .replace(/^./, str => str.toUpperCase());
  }
  
  // Capitalize first letter as fallback
  return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
};

/**
 * Get a user-friendly display value for various data types
 * 
 * @param {any} value - The value to format
 * @param {string} fieldName - Original field name for context
 * @returns {string} - Formatted display value
 */
const getFormattedValue = (value, fieldName = '') => {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return '-';
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    // Special handling for is_completed fields
    if (fieldName === 'is_completed' || fieldName === 'isCompleted') {
      return value ? 'Completed' : 'Pending';
    }
    // Generic boolean display
    return value ? 'Yes' : 'No';
  }
  
  // Handle date values (basic check)
  if (
    typeof value === 'string' && 
    (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{4}-\d{2}-\d{2}T/))
  ) {
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      // If date parsing fails, return the original value
      console.warn(`Failed to parse date: ${value}`, e);
      return value;
    }
  }
  
  // Handle monetary values (checking if the field name suggests money)
  const moneyFields = ['amount', 'pay', 'rate', 'salary', 'wage', 'cost', 'price', 'total'];
  if (
    typeof value === 'number' &&
    moneyFields.some(term => fieldName.toLowerCase().includes(term))
  ) {
    return `$${value.toFixed(2)}`;
  }
  
  // For everything else, return the value as a string
  return String(value);
};

export { 
  getFriendlyFieldName,
  getFormattedValue 
};